import {
  useDeletePartyDocument,
  usePartyDocuments,
  useUpdatePartyDocument,
  useUploadPartyDocument,
} from '@my/directory-api/hooks';
import type {
  PartyDocument,
  PartyDocumentTypeValue,
  UpdatePartyDocumentRequest,
} from '@my/directory-api';
import {
  ConfirmDialog,
  ErrorBanner,
  Loading,
  SelectInput,
  TextInput,
  showToast,
  type SelectInputOption,
} from '@my/ui';
import {
  Check,
  Download,
  Edit,
  ExternalLink,
  FileText,
  Loader2,
  Plus,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { useRef, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';

interface PartyDocumentsSectionProps {
  partyId: string;
}

/** Closed list of document-type values matching the BE `PartyDocumentType` enum. */
const DOCUMENT_TYPE_OPTIONS: PartyDocumentTypeValue[] = [
  'Contract',
  'Finance',
  'Identity',
  'Insurance',
  'Tax',
  'Other',
];

/** Pretty filesize formatter — three sig figs, KB / MB ladder. */
function formatFileSize(bytes: number | null | undefined): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb >= 100 ? 0 : 1)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(mb >= 100 ? 0 : 1)} MB`;
}

/**
 * Drag-and-drop / click-to-upload zone + card list of uploaded
 * documents. Each card shows filename, type badge, size + description,
 * with download (via the BE's short-lived SAS URL on the row), edit
 * metadata (type + description) and delete affordances.
 *
 * Files upload immediately on drop / pick — no staging step. The BE
 * computes a SHA-256 hash and stores the blob in `party-documents`;
 * cache invalidation on success surfaces the new row in the list.
 */
export function PartyDocumentsSection({ partyId }: PartyDocumentsSectionProps) {
  const { t } = useTranslation(['parties', 'common']);

  const listQuery = usePartyDocuments(partyId);
  const uploadMutation = useUploadPartyDocument();
  const updateMutation = useUpdatePartyDocument();
  const deleteMutation = useDeletePartyDocument();

  const isBusy =
    uploadMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;

  // Drag-and-drop visual cue — `dragActive` highlights the drop zone
  // border while a file is being dragged over.
  const [dragActive, setDragActive] = useState(false);
  // Hidden file input reused for click-to-browse so the drop zone can
  // double as a clickable target.
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Edit state — `null` = no editor open, otherwise the document id
  // currently being edited. Description + type only (the file itself
  // is immutable per row; replace = upload again).
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<{
    description: string;
    type: PartyDocumentTypeValue;
  }>({ description: '', type: 'Other' });

  const [pendingDelete, setPendingDelete] = useState<PartyDocument | null>(null);

  // Pre-resolve i18n labels for the type dropdown.
  const typeLabels: Record<PartyDocumentTypeValue, string> = {
    Contract: t('parties:documentType.contract'),
    Finance: t('parties:documentType.finance'),
    Identity: t('parties:documentType.identity'),
    Insurance: t('parties:documentType.insurance'),
    Tax: t('parties:documentType.tax'),
    Other: t('parties:documentType.other'),
  };
  const typeOptions: SelectInputOption[] = DOCUMENT_TYPE_OPTIONS.map((value) => ({
    value,
    label: typeLabels[value],
  }));

  const startEdit = (doc: PartyDocument) => {
    setEditingId(doc.id);
    setEditDraft({
      description: doc.description ?? '',
      type: doc.type,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft({ description: '', type: 'Other' });
  };

  const handleEditSave = async () => {
    if (!editingId) return;
    try {
      const body: UpdatePartyDocumentRequest = {
        documentId: editingId,
        description: editDraft.description.trim() || null,
        type: editDraft.type,
      };
      await updateMutation.mutateAsync({ body, partyId });
      showToast({ type: 'success', message: t('parties:documents.saved') });
      cancelEdit();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('parties:documents.saveFailed'),
      });
    }
  };

  // Upload handler — fires once per file. Default type = Other; the
  // operator can edit-and-reclassify after upload via the pencil icon.
  const uploadFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    for (const file of Array.from(files)) {
      try {
        await uploadMutation.mutateAsync({
          partyId,
          file,
          description: null,
          type: 'Other',
        });
      } catch (err) {
        showToast({
          type: 'error',
          message:
            err instanceof Error
              ? err.message
              : t('parties:documents.uploadFailed', { name: file.name }),
        });
      }
    }
    showToast({ type: 'success', message: t('parties:documents.uploaded') });
  };

  // Drag-and-drop wiring. `preventDefault` on each handler is required
  // for the drop event to fire (browser default is to navigate to the
  // dropped file's URL).
  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };
  const onDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };
  const onDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    await uploadFiles(e.dataTransfer.files);
  };

  const confirmDelete = async () => {
    const target = pendingDelete;
    if (!target) return;
    try {
      await deleteMutation.mutateAsync({ id: target.id, partyId });
      showToast({ type: 'success', message: t('parties:documents.deleted') });
      if (editingId === target.id) cancelEdit();
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('parties:documents.deleteFailed'),
      });
    } finally {
      setPendingDelete(null);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <section className="rounded-sm border border-slate-300 bg-white p-3 shadow-sm">
      <header className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">
          {t('parties:documents.title')}
        </h3>
        {/* +Add — same affordance as clicking the drop zone below; just a
            visual consistency win with the Contacts / Addresses headers
            (both have a +Add). Triggers the hidden file input. */}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isBusy}
          className="flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-purple-600 hover:bg-purple-50 disabled:opacity-50"
        >
          <Plus className="h-3.5 w-3.5" />
          {t('common:actions.add')}
        </button>
      </header>

      {/* Drop zone — click to browse OR drag files in. Highlights when a
          drag is in progress. The hidden <input> below provides the
          actual file picker; clicking the zone proxies its click. */}
      <div
        onClick={() => fileInputRef.current?.click()}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={`mb-3 flex cursor-pointer flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed p-4 text-center transition-colors ${
          dragActive
            ? 'border-blue-500 bg-blue-50'
            : 'border-slate-300 bg-slate-50 hover:border-slate-400'
        } ${isBusy ? 'pointer-events-none opacity-60' : ''}`}
      >
        {uploadMutation.isPending ? (
          <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
        ) : (
          <Upload className="h-5 w-5 text-slate-500" aria-hidden="true" />
        )}
        <p className="text-xs font-medium text-slate-600">
          {t('parties:documents.dropOrClick')}
        </p>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          void uploadFiles(e.target.files);
          // Clear so re-picking the same file fires onChange again.
          e.target.value = '';
        }}
      />

      {listQuery.isPending && <Loading label={t('parties:documents.title')} />}
      {listQuery.isError && (
        <ErrorBanner
          title={t('parties:documents.loadFailed')}
          message={listQuery.error.message}
        />
      )}

      {listQuery.isSuccess && (
        <div className="space-y-2">
          {(listQuery.data ?? []).length === 0 ? (
            <p className="px-3 py-2 text-center text-xs italic text-slate-400">
              {t('parties:documents.empty')}
            </p>
          ) : (
            (listQuery.data ?? []).map((doc) =>
              editingId === doc.id ? (
                <DocumentEditor
                  key={doc.id}
                  doc={doc}
                  draft={editDraft}
                  onChange={setEditDraft}
                  onSave={handleEditSave}
                  onCancel={cancelEdit}
                  onDelete={() => setPendingDelete(doc)}
                  isBusy={isBusy}
                  typeOptions={typeOptions}
                />
              ) : (
                <DocumentCard
                  key={doc.id}
                  doc={doc}
                  typeLabel={typeLabels[doc.type]}
                  rowsLocked={editingId !== null}
                  onEdit={() => startEdit(doc)}
                />
              ),
            )
          )}
        </div>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        tone="danger"
        title={t('parties:documents.delete.title')}
        message={
          pendingDelete
            ? t('parties:documents.delete.message', { name: pendingDelete.fileName })
            : null
        }
        confirmLabel={t('common:actions.delete')}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
        busy={deleteMutation.isPending}
      />
    </section>
  );
}

// ── DocumentCard ────────────────────────────────────────────────────────────

function DocumentCard({
  doc,
  typeLabel,
  rowsLocked,
  onEdit,
}: {
  doc: PartyDocument;
  typeLabel: string;
  rowsLocked: boolean;
  onEdit: () => void;
}) {
  return (
    <div
      className={`flex items-start justify-between gap-2 rounded-md border border-slate-200 bg-white p-3 ${
        rowsLocked ? 'opacity-50' : 'hover:bg-slate-50'
      }`}
    >
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center gap-2">
          <FileText className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" aria-hidden="true" />
          {/* Filename is the primary "view in new tab" affordance — clicking
              opens the SAS URL in a new tab. Browser-renderable formats
              (PDF, images, plain text) display inline; everything else
              triggers a download depending on Content-Disposition. */}
          {doc.downloadUrl ? (
            <a
              href={doc.downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              title={`Open ${doc.fileName}`}
              className="truncate text-sm font-semibold text-blue-600 hover:underline"
            >
              {doc.fileName}
            </a>
          ) : (
            <p className="truncate text-sm font-semibold text-slate-900">{doc.fileName}</p>
          )}
          <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-medium text-purple-700">
            {typeLabel}
          </span>
        </div>
        <p className="text-xs text-slate-500">
          {formatFileSize(doc.fileSizeBytes)}
          {doc.description ? ` · ${doc.description}` : ''}
        </p>
      </div>
      <div className="flex items-center gap-1">
        {doc.downloadUrl && (
          <>
            {/* Open in new tab — for browser-renderable formats (PDF,
                images, text) the browser previews inline; otherwise it
                falls through to download depending on the SAS URL's
                Content-Disposition. The clickable filename above does
                the same thing; the icon button is a secondary affordance
                for when the title isn't obvious as a link. */}
            <a
              href={doc.downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Open ${doc.fileName} in new tab`}
              title={`Open ${doc.fileName} in new tab`}
              className="inline-flex h-7 w-7 items-center justify-center rounded-sm text-slate-600 hover:bg-slate-100"
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
            {/* Force-download hint — `download` attribute asks the
                browser to save with the original filename rather than
                preview. Honoured for same-origin URLs; cross-origin SAS
                URLs may ignore it depending on the Content-Disposition
                returned by the storage backend. */}
            <a
              href={doc.downloadUrl}
              download={doc.fileName}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Download ${doc.fileName}`}
              title={`Download ${doc.fileName}`}
              className="inline-flex h-7 w-7 items-center justify-center rounded-sm text-slate-600 hover:bg-slate-100"
            >
              <Download className="h-3.5 w-3.5" />
            </a>
          </>
        )}
        <button
          type="button"
          aria-label={`Edit ${doc.fileName}`}
          disabled={rowsLocked}
          onClick={onEdit}
          className="inline-flex h-7 w-7 items-center justify-center rounded-sm text-blue-600 hover:bg-blue-50 disabled:opacity-50"
        >
          <Edit className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

// ── DocumentEditor ──────────────────────────────────────────────────────────

interface DocumentEditorProps {
  doc: PartyDocument;
  draft: { description: string; type: PartyDocumentTypeValue };
  onChange: (next: { description: string; type: PartyDocumentTypeValue }) => void;
  onSave: () => void;
  onCancel: () => void;
  onDelete: () => void;
  isBusy: boolean;
  typeOptions: SelectInputOption[];
}

function DocumentEditor({
  doc,
  draft,
  onChange,
  onSave,
  onCancel,
  onDelete,
  isBusy,
  typeOptions,
}: DocumentEditorProps) {
  const { t } = useTranslation(['parties', 'common']);
  return (
    <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
      <div className="flex items-center gap-2">
        <FileText className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />
        <p className="truncate text-sm font-semibold text-slate-900">{doc.fileName}</p>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <SelectInput
          label={t('parties:documents.fields.type')}
          name="document-type"
          value={draft.type}
          options={typeOptions}
          onChange={(e) =>
            onChange({ ...draft, type: e.target.value as PartyDocumentTypeValue })
          }
          disabled={isBusy}
        />
        <TextInput
          label={t('parties:documents.fields.description')}
          name="document-description"
          value={draft.description}
          maxLength={1024}
          onChange={(e) => onChange({ ...draft, description: e.target.value })}
          disabled={isBusy}
        />
      </div>
      <div className="flex items-center justify-between gap-2 pt-1">
        <button
          type="button"
          aria-label={t('common:actions.delete')}
          onClick={onDelete}
          disabled={isBusy}
          className="flex h-7 w-7 items-center justify-center rounded-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label={t('common:actions.cancel')}
            onClick={onCancel}
            disabled={isBusy}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-200 disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            aria-label={t('common:actions.save')}
            onClick={onSave}
            disabled={isBusy}
            className="flex h-7 w-7 items-center justify-center rounded-sm text-green-600 hover:bg-green-50 disabled:opacity-50"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
